import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type { ApiResponse } from '../../menu';

const DASHBOARD_API = '/umbraco/api/dashboards';

export type ActivityRecord = Record<string, unknown>;

export type ActivityRow = {
  caption: string;
  data: ActivityRecord[];
};

export type DashboardBlockLink = {
  title: string | null;
  url: string | null;
  imageUrl: string | null;
  target?: string | null;
};

export type DashboardBlock = {
  type: string;
  key: string;
  title: string | null;
  icon: string | null;
  widthCss: string;
  backgroundColor: string | null;
  backgroundColorDarkTheme: string | null;
  links: DashboardBlockLink[] | null;
  relatedLinkUrl?: string | null;
  relatedLinkCaption?: string | null;
};

export type DashboardLayoutRow = {
  minHeight: number;
  blocks: DashboardBlock[];
};

export type DashboardLayout = {
  rows: DashboardLayoutRow[];
};

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery,
  tagTypes: ['Dashboard'],
  endpoints: (builder) => ({
    getDashboard: builder.query<ActivityRow[], string>({
      query: (nodeKey) => ({
        url: `${DASHBOARD_API}/get`,
        params: { nodeKey },
      }),
      transformResponse: (response: ApiResponse<ActivityRow[]>) => {
        if (response.Code === 1 || response.code === 1) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load dashboard');
      },
      providesTags: (_result, _error, nodeKey) => [{ type: 'Dashboard', id: nodeKey }],
    }),
    getDashboardLayout: builder.query<DashboardLayout, string>({
      query: (nodeKey) => ({
        url: `${DASHBOARD_API}/getlayout`,
        params: { nodeKey },
      }),
      transformResponse: (response: ApiResponse<DashboardLayout>) => {
        if (response.Code === 1 || response.code === 1) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load dashboard layout');
      },
      providesTags: (_result, _error, nodeKey) => [{ type: 'Dashboard', id: `layout-${nodeKey}` }],
    }),
  }),
});

export const { useGetDashboardQuery, useGetDashboardLayoutQuery } = dashboardApi;
